import DOMPurify from 'dompurify';

/**
 * Security utility for input sanitization and validation
 * Prevents HTML injection, XSS, and other input-based attacks
 *
 * @module inputSanitization
 */

/**
 * Sanitize and validate user input to prevent HTML injection
 *
 * @param {string} input - Raw user input
 * @param {object} options - Sanitization options
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input, options = {}) => {
  if (!input || typeof input !== 'string') return '';

  const {
    allowedTags = [], // No HTML tags allowed by default
    maxLength = 100,
    stripTags = true,
    allowSpecialChars = false,
  } = options;

  // Remove any HTML tags using DOMPurify
  let sanitized = stripTags
    ? DOMPurify.sanitize(input, {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true,
      })
    : input;

  // Remove any remaining HTML entities
  sanitized = sanitized
    .replace(/&lt;/g, '')
    .replace(/&gt;/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '')
    .replace(/&#x27;/g, '');

  // Remove special characters if not allowed
  if (!allowSpecialChars) {
    sanitized = sanitized.replace(/[<>{}[\]\\/]/g, '');
  }

  // Trim whitespace
  sanitized = sanitized.trim();

  // Enforce max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Validate name field specifically
 * Name should only contain letters, spaces, hyphens, apostrophes
 *
 * @param {string} name - Name input
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateName = name => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, sanitized: '', error: 'Name is required' };
  }

  // Sanitize first
  const sanitized = sanitizeInput(name, {
    maxLength: 50,
    stripTags: true,
    allowSpecialChars: false,
  });

  // Validate format: only letters, spaces, hyphens, apostrophes, accented characters
  const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;

  if (!sanitized) {
    return {
      isValid: false,
      sanitized: '',
      error: 'Name cannot be empty',
    };
  }

  if (sanitized.length < 2) {
    return {
      isValid: false,
      sanitized,
      error: 'Name must be at least 2 characters',
    };
  }

  if (sanitized.length > 50) {
    return {
      isValid: false,
      sanitized,
      error: 'Name must be less than 50 characters',
    };
  }

  if (!nameRegex.test(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes',
    };
  }

  // Check for suspicious patterns that might indicate XSS attempts
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // event handlers like onclick=
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<a\s+href/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(name)) {
      logSecurityEvent('HTML_INJECTION_ATTEMPT', {
        input: name,
        pattern: pattern.toString(),
        field: 'name',
      });
      return {
        isValid: false,
        sanitized,
        error: 'Invalid characters detected',
      };
    }
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Validate email format
 *
 * @param {string} email - Email input
 * @returns {object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateEmail = email => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, sanitized: '', error: 'Email is required' };
  }

  const sanitized = sanitizeInput(email.toLowerCase(), {
    maxLength: 100,
    stripTags: true,
    allowSpecialChars: true,
  });

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'Please enter a valid email address',
    };
  }

  return { isValid: true, sanitized, error: null };
};

/**
 * Log security events for monitoring
 *
 * @param {string} eventType - Type of security event
 * @param {object} data - Event data
 */
export const logSecurityEvent = (eventType, data) => {
  const logData = {
    timestamp: new Date().toISOString(),
    eventType,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    ...data,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.warn('[SECURITY EVENT]', logData);
  }

  // In production, send to monitoring service
  if (process.env.NODE_ENV === 'production') {
    // TODO: Send to Sentry, CloudWatch, or your logging service
    console.error('[SECURITY EVENT]', logData);

    // Example: Send to backend endpoint
    // fetch('/api/security/log', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(logData),
    // }).catch(err => console.error('Security logging failed:', err));
  }
};

/**
 * Rate limiting helper (client-side)
 * Prevents rapid form submissions
 */
class RateLimiter {
  constructor(maxAttempts = 5, timeWindow = 60000) {
    this.attempts = [];
    this.maxAttempts = maxAttempts;
    this.timeWindow = timeWindow; // 1 minute default
  }

  isAllowed() {
    const now = Date.now();
    // Remove old attempts outside the time window
    this.attempts = this.attempts.filter(time => now - time < this.timeWindow);

    if (this.attempts.length >= this.maxAttempts) {
      logSecurityEvent('RATE_LIMIT_EXCEEDED', {
        attempts: this.attempts.length,
        maxAttempts: this.maxAttempts,
      });
      return false;
    }

    this.attempts.push(now);
    return true;
  }

  getRemainingTime() {
    if (this.attempts.length === 0) return 0;
    const oldestAttempt = Math.min(...this.attempts);
    const elapsed = Date.now() - oldestAttempt;
    return Math.max(0, this.timeWindow - elapsed);
  }

  reset() {
    this.attempts = [];
  }
}

// Export singleton instance for signup form
export const signupRateLimiter = new RateLimiter(5, 60000);
